import {
  TrendUp,
  Users,
  ShoppingCart,
  Wallet,
  ArrowUpRight,
  Package,
} from "@phosphor-icons/react";
import Layout from "@/components/Layout";
import { revenueStats, products } from "@/data/mockData";

function fmt(amount) {
  return `€${amount.toLocaleString("es")}`;
}

export default function Studio() {
  const maxSale = Math.max(...revenueStats.sales.map((s) => s.amount));
  const delta = revenueStats.mtd - revenueStats.mtdPrev;
  const deltaPct = ((delta / revenueStats.mtdPrev) * 100).toFixed(1);
  const topProducts = revenueStats.topProducts
    .map((id) => products.find((p) => p.id === id))
    .filter(Boolean);

  return (
    <Layout wide>
      <div data-testid="studio-page">
        <header className="p-4 md:p-8 border-b border-[#27272A] flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.3em] mb-2">
              CREATOR STUDIO · FEB 2026
            </div>
            <h1 className="font-display text-4xl md:text-6xl font-black tracking-tighter uppercase leading-none">
              Tus <span className="text-[#FF3E00]">ganancias</span>.
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button className="btn-brutal font-mono uppercase text-[10px] tracking-[0.22em] px-4 py-3 border border-[#F4F4F5] hover:bg-[#F4F4F5] hover:text-black">
              Exportar CSV
            </button>
            <button
              data-testid="payout-btn"
              className="btn-brutal font-mono uppercase text-[10px] tracking-[0.22em] px-4 py-3 bg-[#FF3E00] text-white font-bold"
            >
              Solicitar payout
            </button>
          </div>
        </header>

        {/* KPI row */}
        <section className="grid grid-cols-2 md:grid-cols-4 border-b border-[#27272A]">
          <Kpi
            label="MES ACTUAL"
            value={fmt(revenueStats.mtd)}
            trend={`+${deltaPct}%`}
            trendUp
            icon={TrendUp}
            color="#FF3E00"
          />
          <Kpi
            label="TOTAL HISTÓRICO"
            value={fmt(revenueStats.lifetime)}
            icon={Wallet}
            color="#00FF66"
            divider
          />
          <Kpi
            label="MRR SUSCRIPCIONES"
            value={fmt(revenueStats.subscribers.mrr)}
            trend={`+${revenueStats.subscribers.trend} nuevos`}
            icon={Users}
            color="#0033FF"
            divider
          />
          <Kpi
            label="PRÓXIMO PAYOUT"
            value={fmt(revenueStats.payoutNext)}
            trend={revenueStats.payoutOn}
            icon={Package}
            color="#FF0033"
            divider
          />
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-3">
          {/* Sales chart */}
          <section className="lg:col-span-2 p-4 md:p-6 border-b lg:border-b-0 lg:border-r border-[#27272A]">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display font-black uppercase tracking-tight text-xl">
                Ventas · últimos 7 días
              </h2>
              <span className="font-mono text-[10px] text-[#00FF66] tracking-wider">
                ↑ {deltaPct}% vs. semana anterior
              </span>
            </div>

            <div className="flex items-end gap-2 md:gap-3 h-56 border-b border-[#27272A] pb-2">
              {revenueStats.sales.map((s, i) => {
                const h = (s.amount / maxSale) * 100;
                const isMax = s.amount === maxSale;
                return (
                  <div
                    key={s.day}
                    className="flex-1 relative group flex items-end"
                    style={{ height: "100%" }}
                  >
                    <div
                      className={`w-full relative transition-colors fade-up ${
                        isMax
                          ? "bg-[#FF3E00]"
                          : "bg-[#27272A] group-hover:bg-[#F4F4F5]"
                      }`}
                      style={{
                        height: `${h}%`,
                        minHeight: "4px",
                        animationDelay: `${i * 0.05}s`,
                      }}
                    >
                      <span className="absolute -top-6 left-1/2 -translate-x-1/2 font-mono text-[10px] text-[#A1A1AA] opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        {fmt(s.amount)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-2 md:gap-3 mt-2">
              {revenueStats.sales.map((s) => (
                <div
                  key={s.day}
                  className="flex-1 text-center font-mono text-[10px] text-[#A1A1AA] tracking-wider"
                >
                  {s.day}
                </div>
              ))}
            </div>

            {/* Breakdown */}
            <div className="mt-10">
              <h3 className="font-display font-bold uppercase tracking-tight mb-4">
                Desglose por fuente
              </h3>
              <div className="space-y-3">
                {revenueStats.breakdown.map((b) => (
                  <div key={b.label}>
                    <div className="flex items-center justify-between gap-2 font-mono text-[11px] tracking-wider">
                      <span className="text-[#A1A1AA]">{b.label}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-white">{fmt(b.amount)}</span>
                        <span
                          className="font-bold"
                          style={{ color: b.color }}
                        >
                          {b.pct}%
                        </span>
                      </div>
                    </div>
                    <div className="mt-1 h-1 bg-[#27272A]">
                      <div
                        className="h-full transition-all"
                        style={{ width: `${b.pct}%`, background: b.color }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Subscribers + Top products */}
          <aside className="p-4 md:p-6 space-y-6">
            <div className="border border-[#27272A] p-4">
              <div className="flex items-center justify-between">
                <h3 className="font-display font-bold uppercase tracking-tight">
                  Suscriptores
                </h3>
                <Users size={16} className="text-[#FF3E00]" weight="fill" />
              </div>
              <div className="font-display font-black text-5xl mt-2">
                {revenueStats.subscribers.total}
              </div>
              <div className="font-mono text-[10px] text-[#00FF66] tracking-wider mt-1">
                ↑ +{revenueStats.subscribers.trend} esta semana
              </div>
              <div className="font-mono text-[10px] text-[#A1A1AA] tracking-wider mt-3 flex items-center justify-between pt-3 border-t border-[#27272A]">
                <span>MRR</span>
                <span className="text-white font-bold">
                  {fmt(revenueStats.subscribers.mrr)}
                </span>
              </div>
            </div>

            <div className="border border-[#27272A]">
              <header className="p-4 border-b border-[#27272A] flex items-center justify-between">
                <h3 className="font-display font-bold uppercase tracking-tight">
                  Top productos
                </h3>
                <ShoppingCart size={14} className="text-[#A1A1AA]" />
              </header>
              <ul>
                {topProducts.map((p, i) => (
                  <li
                    key={p.id}
                    className="flex items-center gap-3 p-3 border-b border-[#27272A] last:border-b-0"
                  >
                    <span className="font-mono text-[10px] text-[#A1A1AA] tracking-wider">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <img
                      src={p.image}
                      alt=""
                      className="h-10 w-10 object-cover border border-[#27272A] shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold text-xs truncate">
                        {p.title}
                      </div>
                      <div className="font-mono text-[9px] text-[#A1A1AA] tracking-wider">
                        {p.sold} ventas
                      </div>
                    </div>
                    <ArrowUpRight size={14} className="text-[#A1A1AA]" />
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>

        {/* Recent orders */}
        <section className="p-4 md:p-6 border-t border-[#27272A]">
          <h2 className="font-display font-black uppercase tracking-tight text-xl mb-4">
            Órdenes recientes
          </h2>
          <div className="border border-[#27272A]">
            <div className="grid grid-cols-[1fr_1fr_auto_auto] gap-3 px-4 py-3 border-b border-[#27272A] font-mono text-[10px] text-[#A1A1AA] tracking-[0.22em]">
              <span>CLIENTE / ITEM</span>
              <span className="hidden md:block">ESTADO</span>
              <span>MONTO</span>
              <span>CUANDO</span>
            </div>
            {revenueStats.recentOrders.map((o) => (
              <div
                key={o.id}
                className="grid grid-cols-[1fr_1fr_auto_auto] gap-3 px-4 py-3 border-b border-[#27272A] last:border-b-0 hover:bg-[#0a0a0a] transition-colors items-center"
              >
                <div className="min-w-0">
                  <div className="font-display font-bold text-sm">{o.buyer}</div>
                  <div className="font-mono text-[10px] text-[#A1A1AA] tracking-wider truncate">
                    {o.item}
                  </div>
                </div>
                <span className="hidden md:inline-flex font-mono text-[10px] tracking-[0.2em] uppercase text-[#00FF66] border border-[#00FF66]/30 px-2 py-1 w-fit">
                  {o.status}
                </span>
                <span className="font-display font-black text-[#FF3E00]">
                  {fmt(o.amount)}
                </span>
                <span className="font-mono text-[10px] text-[#A1A1AA] tracking-wider whitespace-nowrap">
                  {o.time}
                </span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </Layout>
  );
}

function Kpi({ label, value, trend, trendUp, icon: Icon, color, divider }) {
  return (
    <div
      className={`p-5 md:p-6 ${
        divider ? "md:border-l border-t md:border-t-0 border-[#27272A]" : ""
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.22em]">
          {label}
        </span>
        <Icon size={14} style={{ color }} weight="fill" />
      </div>
      <div className="font-display font-black text-2xl md:text-3xl leading-none">
        {value}
      </div>
      {trend ? (
        <div
          className={`font-mono text-[10px] mt-1 tracking-wider ${
            trendUp ? "text-[#00FF66]" : "text-[#A1A1AA]"
          }`}
        >
          {trendUp ? "↑" : "·"} {trend}
        </div>
      ) : null}
    </div>
  );
}
